import { COUNTRIES } from '@/lib/countries';

/**
 * Contributors type their country by hand, in French or in English, so the map
 * matches on a folded form of the name and keeps an alias list for the spellings
 * the reference list does not carry.
 */
export function foldCountryName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Spellings seen in the field, mapped to their ISO 3166-1 alpha-2 code. */
const ALIASES: Readonly<Record<string, string>> = {
  'rd congo': 'CD',
  rdc: 'CD',
  drc: 'CD',
  'congo kinshasa': 'CD',
  'republique democratique du congo': 'CD',
  'democratic republic of the congo': 'CD',
  'congo brazzaville': 'CG',
  'republique du congo': 'CG',
  'cote d ivoire': 'CI',
  'ivory coast': 'CI',
  civ: 'CI',
  'burkina': 'BF',
  'cap vert': 'CV',
  'cabo verde': 'CV',
  'guinee bissau': 'GW',
  'guinee equatoriale': 'GQ',
  'afrique du sud': 'ZA',
  'south africa': 'ZA',
  'etats unis': 'US',
  'united states of america': 'US',
  usa: 'US',
  'royaume uni': 'GB',
  uk: 'GB',
  'grande bretagne': 'GB',
  allemagne: 'DE',
  belgique: 'BE',
  espagne: 'ES',
  italie: 'IT',
  'pays bas': 'NL',
  suisse: 'CH',
  centrafrique: 'CF',
  'republique centrafricaine': 'CF',
  tchad: 'TD',
  egypte: 'EG',
  ethiopie: 'ET',
  kenya: 'KE',
  maroc: 'MA',
  mauritanie: 'MR',
  'sierra leone': 'SL',
  soudan: 'SD',
  tanzanie: 'TZ',
  zambie: 'ZM',
  zimbabwe: 'ZW',
};

const BY_FOLDED_NAME: ReadonlyMap<string, string> = new Map([
  ...COUNTRIES.map((country) => [foldCountryName(country.name), country.iso] as const),
  ...Object.entries(ALIASES),
]);

/** The codes themselves: production stores "TG" as often as "Togo". */
const ISO_CODES: ReadonlySet<string> = new Set(COUNTRIES.map((country) => country.iso.toLowerCase()));

/**
 * The ISO code a reported country points to, or null when unknown. A country
 * recorded under several spellings, code included, resolves to one code, so the
 * map counts it once instead of splitting it.
 */
export function isoOfCountryName(name: string): string | null {
  const folded = foldCountryName(name);
  if (!folded) return null;
  const byName = BY_FOLDED_NAME.get(folded);
  if (byName) return byName;
  const compact = folded.replace(/\s+/g, '');
  return compact.length === 2 && ISO_CODES.has(compact) ? compact.toUpperCase() : null;
}
